import { dateFnsLocalizer as createDateFnsLocalizer } from "react-big-calendar";
import { format, startOfWeek, getDay } from "date-fns";
import { enUS, enGB, de, fr, da, Locale } from "date-fns/locale";
import i18next from "i18next";
import { TZDate } from "@date-fns/tz";

const localeMap: { [key: string]: Locale } = {
  en: enUS,
  "en-gb": enGB,
  de: de,
  fr: fr,
  da: da
};

export const getDateFnsLocaleFromI18n = (language: string | null | undefined): Locale => {
  if (!language) return enUS;

  const normalized = language.toLowerCase().trim();
  return localeMap[normalized] || localeMap[normalized.split("-")[0]] || enUS;
};

export const getCalendarStringsFromLocale = (locale: Locale) => {
  const months: string[] = [];
  const shortMonths: string[] = [];
  const days: string[] = [];
  const shortDays: string[] = [];

  for (let i = 0; i < 12; i++) {
    const date = new Date(2024, i, 1);
    months.push(format(date, "MMMM", { locale }));
    shortMonths.push(format(date, "MMM", { locale }));
  }

  const baseDate = new Date(2024, 0, 7);
  for (let i = 0; i < 7; i++) {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + i);
    days.push(format(date, "EEEE", { locale }));
    shortDays.push(format(date, "EEE", { locale }));
  }

  return {
    months,
    shortMonths,
    days,
    shortDays
  };
};

export const changeLanguageDate = (_language: string | null = "en"): void => {
  // Locale changing logic can be implemented here if needed
};

export const convertMomentFormatToDateFnsFormat = (momentFormat: string): string => {
  const formatMapping: { [key: string]: string } = {
    "h A": "h a",
    "h:mm A": "h:mm a",
    "MMM D": "MMM d",
    "MMMM YYYY": "MMMM yyyy",
    dddd: "EEEE",
    ddd: "EEE",
    DD: "dd",
    D: "d",
    YYYY: "yyyy",
    YY: "yy",
    MM: "MM",
    M: "M",
    MMM: "MMM",
    MMMM: "MMMM"
  };

  let dateFnsFormat = momentFormat;
  Object.entries(formatMapping).forEach(([momentFormat, dateFnsFormatStr]) => {
    dateFnsFormat = dateFnsFormat.replace(new RegExp(momentFormat, "g"), dateFnsFormatStr);
  });

  return dateFnsFormat;
};

export const dateFnsLocalizer = (timezone?: string) =>
  createDateFnsLocalizer({
    format: (date: Date, formatStr: string) => {
      const locale = getDateFnsLocaleFromI18n(i18next.language);
      const formatMapping: { [key: string]: string } = {
        "h A": "h a",
        "h:mm A": "h:mm a",
        "MMM D": "MMM d",
        "MMMM YYYY": "MMMM yyyy",
        dddd: "EEEE",
        ddd: "EEE",
        DD: "dd",
        D: "d",
        YYYY: "yyyy",
        YY: "yy",
        MM: "MM",
        M: "M",
        MMM: "MMM",
        MMMM: "MMMM"
      };

      let dateFnsFormat = formatStr;
      Object.entries(formatMapping).forEach(([momentFormat, dateFnsFormatStr]) => {
        dateFnsFormat = dateFnsFormat.replace(new RegExp(momentFormat, "g"), dateFnsFormatStr);
      });

      try {
        return format(new Date(date), dateFnsFormat, {
          locale: locale
        });
      } catch (error) {
        console.warn(`Date formatting error with format "${dateFnsFormat}":`, error);
        return format(date, "MMM d, yyyy", {
          locale: locale
        });
      }
    },

    parse: (dateString: string, _formatStr: string) => {
      try {
        return new Date(dateString);
      } catch (error) {
        console.warn(`Date parsing error:`, error);
        return new Date(dateString);
      }
    },

    startOfWeek: (date: Date) => {
      return startOfWeek(date);
    },

    getDay: (date: Date) => {
      return getDay(date);
    },

    locales: localeMap
  });

export default dateFnsLocalizer;
